import { useCallback, useEffect, useState } from 'react';
import { ACTIVE, api, type Who } from './api.ts';
import { AccountPage } from './pages/Account.tsx';
import { Cancel } from './pages/Cancel.tsx';
import { FindId, FindPassword } from './pages/Find.tsx';
import { Home } from './pages/Home.tsx';
import { Login } from './pages/Login.tsx';
import { Plan } from './pages/Plan.tsx';
import { SignupStep1, SignupStep2, type SignupStart } from './pages/Signup.tsx';
import { Social } from './pages/Social.tsx';
import { Tools } from './pages/Tools.tsx';
import { box } from './ui.tsx';
import { setUsageOn, usageScreen } from './usage.ts';

/**
 * 기반 공사 3단계 화면 길잡이. 디자인은 하지 않고 흐름만 잇는다.
 * - 로그인 안 함: 로그인, 가입, 찾기, 간편 로그인 돌아오는 곳만
 * - 로그인 함, 요금제가 활성화 안 됨(무료 시작 전, 이용 막힘, 해지됨): 요금제와 내 계정만(2026-11-02 대표님)
 * - 활성화됨: 처음 화면, 내 계정, 요금제
 * - 쓰는 중이면(누르거나 입력하면) 5분에 한 번 서버에 알려 "로그인 상태 유지"를 안 한 곳의 30분을 다시 세게 한다.
 * - 로그인한 동안 사용 기록(화면, 버튼, 머문 시간)을 모은다(usage.ts, 4단계).
 */
const PUBLIC = new Set(['/login', '/signup', '/signup/business', '/social', '/find/id', '/find/password']);
const NOTES: Record<string, string> = {
  'linked=kakao': '카카오를 이 계정에 연결했어요.',
  'linked=naver': '네이버를 이 계정에 연결했어요.',
  'link_error=taken': '이 계정은 다른 일손이 계정에 연결되어 있어요.',
  'social_error=1': '카카오나 네이버 로그인이 끝나지 않았어요. 한 번 더 눌러 보시겠어요?'
};

function noteFromUrl(): string | null {
  const q = window.location.search.slice(1);
  return Object.entries(NOTES).find(([k]) => q.includes(k))?.[1] ?? null;
}

export function App() {
  const [path, setPath] = useState(window.location.pathname);
  const [who, setWho] = useState<Who | null | undefined>(undefined);
  const [notice, setNotice] = useState<string | null>(noteFromUrl());
  const [start, setStart] = useState<SignupStart | null>(null);
  const [linkTicket, setLinkTicket] = useState<string | null>(null);

  const go = useCallback((p: string) => {
    window.history.pushState(null, '', p);
    setPath(p.split('?')[0] ?? p);
  }, []);

  const refresh = useCallback(async () => {
    const r = await api<{ who: Who }>('GET', '/api/auth/me');
    if (r.ok) setWho(r.who);
    else {
      setWho(null);
      if (r.code === 'logged_out_elsewhere') setNotice(r.message);
    }
  }, []);

  useEffect(() => {
    void refresh();
    const onPop = () => setPath(window.location.pathname);
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [refresh]);

  useEffect(() => {
    setUsageOn(Boolean(who), window.location.pathname);
  }, [who]);

  useEffect(() => {
    usageScreen(path);
  }, [path]);

  useEffect(() => {
    if (!who) return;
    let last = 0;
    const ping = () => {
      if (Date.now() - last < 5 * 60 * 1000) return;
      last = Date.now();
      void api('POST', '/api/auth/ping').then((r) => {
        if (!r.ok && r.status === 401) void refresh();
      });
    };
    window.addEventListener('click', ping);
    window.addEventListener('keydown', ping);
    return () => {
      window.removeEventListener('click', ping);
      window.removeEventListener('keydown', ping);
    };
  }, [who, refresh]);

  const signedIn = (w: Who, note?: string | null) => {
    setWho(w);
    setNotice(note ?? null);
    setLinkTicket(null);
    go(ACTIVE.has(w.service_status) ? '/' : '/plan');
  };

  if (who === undefined) return <main style={box}>일손이</main>;

  if (!who) {
    if (!PUBLIC.has(path)) {
      window.history.replaceState(null, '', '/login');
      if (path !== '/login') setPath('/login');
    }
    if (path === '/signup') return <SignupStep1 go={go} onReady={(s) => { setStart(s); go('/signup/business'); }} />;
    if (path === '/signup/business' && start) {
      return <SignupStep2 start={start} go={go} onSignedIn={signedIn} onLinkLogin={(t) => { setLinkTicket(t); go('/login'); }} />;
    }
    if (path === '/social') {
      const ticket = new URLSearchParams(window.location.search).get('ticket') ?? '';
      return (
        <Social
          ticket={ticket}
          onSignedIn={signedIn}
          onNeedSignup={(provider) => { setStart({ method: provider, ticket }); go('/signup/business'); }}
        />
      );
    }
    if (path === '/find/id') return <FindId go={go} />;
    if (path === '/find/password') return <FindPassword go={go} />;
    return <Login go={go} onSignedIn={signedIn} notice={notice} linkTicket={linkTicket} />;
  }

  const active = ACTIVE.has(who.service_status);
  if (path === '/account') {
    return <AccountPage who={who} go={go} note={notice} onSignedOut={() => { setWho(null); setNotice(null); go('/login'); }} />;
  }
  // 해지 화면은 요금제 화면에 버튼 없이, 자주 묻는 질문(문의하기를 만들 때)에서 이 주소로 잇는다
  if (path === '/cancel' && (active || who.service_status === '해지 예약')) return <Cancel who={who} go={go} refresh={refresh} />;
  if (path === '/plan' || !active) return <Plan who={who} go={go} refresh={refresh} />;
  // 5단계 도구 시험 화면(시험용 서버에서만 서버 쪽이 켜짐)
  if (path === '/tools') return <Tools go={go} />;
  return <Home who={who} go={go} note={notice} />;
}
